import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom"; // Add useSearchParams import
import { useQuery, useQueryClient } from "@tanstack/react-query"; // Add useQueryClient import
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Star, Share2, StarIcon, Lock, Wand2, Coins, Check, Volume2 } from "lucide-react";
import { SiLinkedin, SiFacebook, SiX  } from "react-icons/si";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/hooks/useLanguage";
import { useFeatureLimits } from "@/hooks/useFeatureLimits";
import NavigationBar from "@/components/NavigationBar";
import StoryHeader from "@/components/read-story/StoryHeader";
import StoryNavigation from "@/components/read-story/StoryNavigation";
import StoryContent from "@/components/read-story/StoryContent";
import BottomNavigation from "@/components/dashboard/BottomNavigation";
import ExtractedCharacters from "@/components/read-story/ExtractedCharacters"; // Add this import
import MediaGenerationToolbar from "@/components/story/MediaGenerationToolbar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import LoadingStorySection from "@/components/story-editor/LoadingStorySection"; // Add this import
import SmartLoadingSection from "@/components/story-editor/SmartLoadingSection"; // Add this import
import { logger } from "@/lib/logger";

const ReadStory = () => {
  const { storyId } = useParams();
  const [searchParams] = useSearchParams(); // Add this to get URL parameters
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();
  const { isFeatureEnabled } = useFeatureLimits();
  const queryClient = useQueryClient(); // Initialize the queryClient
  const [storyParts, setStoryParts] = useState([]);
  const [session, setSession] = useState<null | any>(null);
  const [userRating, setUserRating] = useState<number | null>(null);
  const [isRating, setIsRating] = useState(false);
  const [averageRating, setAverageRating] = useState<number | null>(null);
  const [isSharing, setIsSharing] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [customCaption, setCustomCaption] = useState("");
  const [selectedPlatform, setSelectedPlatform] = useState<string>("");
  const [privacyLevel, setPrivacyLevel] = useState<string>("private");
  const [isContinueUpgradeDialogOpen, setIsContinueUpgradeDialogOpen] = useState(false);
  const [isShareUpgradeDialogOpen, setIsShareUpgradeDialogOpen] = useState(false);
  const [isGeneratingContinuation, setIsGeneratingContinuation] = useState(false); // Add this line
  
  // Image generation state variables
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [userCredits, setUserCredits] = useState<number>(0);
  const [requiredCredits, setRequiredCredits] = useState<number>(10);
  const [isImageSelectionOpen, setIsImageSelectionOpen] = useState(false);
  const [generatedImages, setGeneratedImages] = useState<{id: string, url: string}[]>([]);
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  const [imageGenerationProgress, setImageGenerationProgress] = useState(0);
  const [generationErrors, setGenerationErrors] = useState<string[]>([]);
  const [originalImageUrl, setOriginalImageUrl] = useState<string | null>(null);
  const [storyImage, setStoryImage] = useState<string | null>(null);
  
  // Audio generation state variables
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioGenerationError, setAudioGenerationError] = useState<string | null>(null);
  
  // Constants for image generation
  const NUM_IMAGES_TO_GENERATE = 4;
  const IMAGE_GENERATION_REWARD_ID = 'fa7d4035-d284-448b-8d1f-3341fb5d182d';
  
  // Constants for TTS generation
  const AUDIO_GENERATION_REWARD_ID = 'b5d8c032-e2d7-4f9b-8a5c-6ee3b7eff1c7';

  // Missing currentPage state
  const [currentPage, setCurrentPage] = useState<number>(0);

  // Add viewMode state
  const [viewMode, setViewMode] = useState<"paginated" | "entire">("paginated");

  // Add state for rewriting
  const [isRewriting, setIsRewriting] = useState(false);

  // Add state to track rewrites available
  const [storyRewritesAvailable, setStoryRewritesAvailable] = useState<number>(0);

  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setSession(session);
    };
    
    checkSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      // Don't redirect non-authenticated users for story viewing
      // They will only be unable to use features requiring auth
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const { data: story, isLoading: isStoryLoading, refetch: refetchStory } = useQuery({
    queryKey: ['story', storyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('stories')
        .select('*')
        .eq('id', storyId)
        .maybeSingle();

      if (error) throw error;
      
      if (data) {
        setPrivacyLevel(data.privacy_level || "private");
      }
      return data;
    },
  });

  // Add separate effect for checking if story is viewable by current user
  useEffect(() => {
    // This will run once story data is loaded
    const checkStoryAccess = async () => {
      if (!story) return; // Wait until story data is loaded
       
      // First, ensure we have the latest session data
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      
      // If we have a session, the user is logged in and can access
      if (currentSession) {
        // Only update session if it's actually different to prevent infinite loop
        if (JSON.stringify(currentSession) !== JSON.stringify(session)) {
          setSession(currentSession); // Update the session state
        }
        return; // User is authenticated, allow access
      }

      // For non-authenticated users, check if the story is either public
      // or has been shared at least once
      if (story.privacy_level === "public") {
        // Public stories are always accessible
        return;
      }
      
      // Check if the story has been shared
      const { data: storyShares, error: sharesError } = await supabase
        .from('story_shares')
        .select('id')
        .eq('story_id', storyId)
        .limit(1);
      
      if (sharesError) {
        logger.error('Error checking story shares:', sharesError);
      }
      
      // If the story has shares, allow access
      if (storyShares && storyShares.length > 0) {
        return;
      }
      
      // If no shares found and not public, redirect to login
      toast({
        variant: "destructive",
        title: t('dashboard.access.restricted'),
        description: t('dashboard.access.loginRequired'),
      });
      navigate('/auth');
      
      // If user is logged in, they can access according to existing RLS policies
    };
    
    // Add a debounce to prevent multiple calls in quick succession
    const timeoutId = setTimeout(checkStoryAccess, 100);
    
    return () => clearTimeout(timeoutId);
  }, [story, session, navigate, toast, t, storyId, supabase]);

  const { 
    data: storyContent, 
    isLoading: isContentLoading,
    refetch: refetchStoryContent // Extract the refetch function for story content
  } = useQuery({
    queryKey: ['story-content', storyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('story_content')
        .select('*')
        .eq('story_id', storyId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data;
    },
  });

  const { data: storyChain } = useQuery({
    queryKey: ['story-chain', storyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('story_chain')
        .select(`
          id,
          story_id,
          parent_story_id,
          sequence_number,
          stories:story_id (
            id,
            title
          )
        `)
        .eq('story_id', storyId)
        .order('sequence_number', { ascending: true });

      if (error) throw error;
      return data;
    },
    meta: {
      onSuccess: (data) => {
        if (data) {
          const parts = data.map(chain => ({
            id: chain.stories?.id || '',
            title: chain.stories?.title || '',
            sequence_number: chain.sequence_number,
          })).filter(part => part.id);
          setStoryParts(parts);
        }
      }
    }
  });

  // Update state when story data changes (moved after story is defined)
  useEffect(() => {
    if (story?.rewrites_available !== undefined) {
      setStoryRewritesAvailable(story.rewrites_available);
    }
  }, [story?.rewrites_available]);

  useEffect(() => {
    const loadUserRating = async () => {

      if (!storyId) return;

      const { data: storyData, error: storyError } = await supabase
        .from('stories')
        .select('average_rating')
        .eq('id', storyId)
        .single();
        
      if (!storyError && storyData) {
        setAverageRating(storyData.average_rating);
      }

      if (!storyId) return;

      const { data, error } = await supabase
        .from('story_ratings')
        .select('rating')
        .eq('story_id', storyId)
        //.eq('user_id', session.user.id)
        .maybeSingle();

      if (!error && data) {
        setUserRating(data.rating);
      }
    };

    if (story?.finished_at) {
      loadUserRating();
    }
  }, [storyId, session?.user, story?.finished_at]);

  useEffect(() => {
    const recordView = async () => {
      // Only record view if we have both story and session data
      if (!storyId || !session?.user || !story) return;
      
      // Don't record views when the user is viewing their own story
      if (session.user.id === story.user_id) return;
      
      try {
        // Call the record_story_view RPC function
        const { error } = await supabase.rpc(
          'record_story_view' as any,
          {
            p_story_id: storyId,
            p_viewer_id: session.user.id
          }
        );
        
        if (error) {
          logger.error('Error recording view:', error);
        }
      } catch (err) {
        logger.error('Failed to record story view:', err);
      }
    };
    
    recordView();
  }, [storyId, session?.user, story]);

  // Load story image when content loads
  useEffect(() => {
    if (storyContent && storyContent.length > 0 && currentPage >= 0) {
      const currentContentItem = storyContent[currentPage];
      if (currentContentItem?.image_url) {
        setStoryImage(currentContentItem.image_url);
      } else {
        setStoryImage(null);
      }
    }
  }, [storyContent, currentPage]);
  
  // Load story audio when content loads
  useEffect(() => {
    if (storyContent && storyContent.length > 0 && currentPage >= 0) {
      const currentContentItem = storyContent[currentPage];
      // Always set to either the value or null, ensuring it's cleared when none exists
      setAudioUrl(currentContentItem?.audio_url || null);
    }
  }, [storyContent, currentPage]);
  
  // Fetch user credits and required credits
  useEffect(() => {
    const fetchCredits = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        
        // Fetch user credits and reward types in parallel
        const [profileResp, rewardResp] = await Promise.all([
          supabase.from('profiles').select('credits').eq('id', user.id).single(),
          supabase.from('reward_types').select('credits').eq('id', IMAGE_GENERATION_REWARD_ID).single()
        ]);
        
        if (profileResp.data) {
          setUserCredits(profileResp.data.credits || 0);
        }
        
        if (rewardResp.data) {
          setRequiredCredits(rewardResp.data.credits || 1);
        } else {
          setRequiredCredits(1); // Fallback
        }
      } catch (err) {
        logger.error('Error fetching credits:', err);
      }
    };
    
    fetchCredits();
  }, []);

  // Handle rewrite completion with a more robust refresh mechanism
const handleRewritten = async (updatedRewritesAvailable?: number) => {
  setIsRewriting(false);
  
  // Update local state with new rewrites count if provided
  if (updatedRewritesAvailable !== undefined) {
    setStoryRewritesAvailable(updatedRewritesAvailable);
  }
  
  // Show success notification before refreshing
  toast({
    title: t('storyRewrite.rewriteComplete'),
    description: t('storyRewrite.refreshingContent'),
  });
  
  try {
    // Use react-query to invalidate all queries related to this story
    // This should be sufficient now that the underlying issue is fixed
    queryClient.invalidateQueries({ queryKey: ['story', storyId] });
    queryClient.invalidateQueries({ queryKey: ['story-content', storyId] });
    queryClient.invalidateQueries({ queryKey: ['story-chain', storyId] });
    
    // Wait a moment for the invalidation to trigger refetching
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Manually refetch the story content as an additional safety measure
    await refetchStoryContent();
    await refetchStory();
    
    // No need for the aggressive page reload fallbacks anymore
  } catch (error) {
    logger.error("Error refreshing after rewrite:", error);
    
    // Keep the manual refresh button as a final fallback
    toast({
      title: t('storyRewrite.refreshFailed'),
      description: t('storyRewrite.pleaseRefreshManually'),
      action: (
        <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
          {t('refresh')}
        </Button>
      ),
    });
  }
};

  const handleRate = async (rating: number) => {
    if (isRating || !session?.user) return;
    setIsRating(true);
    
    try {
      const { error } = await supabase
        .from('story_ratings')
        .upsert(
          {
            story_id: storyId,
            rating,
            user_id: session.user.id
          },
          {
            onConflict: 'story_id,user_id'
          }
        );

      if (error) throw error;

      const { data: updatedStory, error: storyError } = await supabase
        .from('stories')
        .select('average_rating')
        .eq('id', storyId)
        .single();

      if (storyError) throw storyError;

      setUserRating(rating);
      if (updatedStory) {
        setAverageRating(updatedStory.average_rating);
      }

      toast({
        title: t('story.ratings.submitted'),
        description: t('story.ratings.thankYou'),
      });
    } catch (error) {
      logger.error('Error rating story:', error);
      toast({
        variant: "destructive",
        title: t('story.ratings.error'),
        description: t('story.ratings.failedToSubmit'),
      });
    } finally {
      setIsRating(false);
    }
  };

  const handleShareClick = () => {
    if (!isFeatureEnabled('story_features', 'share')) {
      setIsShareUpgradeDialogOpen(true);
      return;
    }
    setShareDialogOpen(true);
  };

  const handleShare = async () => {
    logger.log('=== Share Process Debug ===');
    logger.log('Initial state:', {
      storyId,
      isSharing,
      privacyLevel,
      selectedPlatform,
      sessionExists: !!session,
      userId: session?.user?.id,
      storyPrivacyLevel: story?.privacy_level
    });

    if (!session?.user) {
      logger.log('No session found, redirecting to login');
      toast({
        variant: "destructive",
        title: t('story.sharing.authRequired'),
        description: t('story.sharing.authMessage'),
      });
      navigate('/login');
      return;
    }

    if (!storyId || isSharing) {
      logger.log('Share blocked due to:', {
        noStoryId: !storyId,
        isSharing
      });
      return;
    }

    setIsSharing(true);
    logger.log('Starting share process for story:', storyId);

    try {
      // First verify story ownership
      const { data: storyData, error: storyError } = await supabase
        .from('stories')
        .select('user_id, privacy_level')
        .eq('id', storyId)
        .maybeSingle();

      logger.log('Story ownership check:', {
        data: storyData,
        error: storyError,
        isOwner: storyData?.user_id === session.user.id
      });

      if (storyError) {
        throw new Error('Failed to verify story ownership');
      }

      if (storyData?.user_id !== session.user.id) {
        throw new Error('You can only share stories you own');
      }

      // Update privacy level if needed
      if (privacyLevel !== "private" && story?.privacy_level !== privacyLevel) {
        logger.log('Attempting privacy update:', {
          from: story?.privacy_level,
          to: privacyLevel
        });

        const { error: privacyError } = await supabase
          .from('stories')
          .update({ 
            privacy_level: privacyLevel 
          })
          .eq('id', storyId)
          .eq('user_id', session.user.id)
          .single();

        if (privacyError) {
          logger.error('Privacy update error:', privacyError);
          throw new Error('Failed to update story privacy settings');
        }
      }

      // Generate share URL
      const storyUrl = `${window.location.origin}/read/${storyId}`;
      let shareUrl = '';
      
      // Check if we're on localhost or a real domain
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

      switch (selectedPlatform) {
        case 'facebook':
          // Use the Feed Dialog API for Facebook which works better with localhost
          // This will explicitly pass the URL and custom text to Facebook
          shareUrl = `https://www.facebook.com/dialog/feed?` + 
            `app_id=9159974227381637` + // Replace with your Facebook App ID if you have one
            `&link=${encodeURIComponent(storyUrl)}` +
            `&quote=${encodeURIComponent(customCaption || story?.title || '')}` +
            `&hashtag=%23TaleCraft` +
            `&display=popup` +
            `&redirect_uri=${encodeURIComponent(window.location.origin + '/read/' + storyId)}`;
          break;
        case 'twitter':
          shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(storyUrl)}&text=${encodeURIComponent(customCaption || story?.title || '')}`;
          break;
        case 'linkedin':
          // Enhanced LinkedIn sharing with more parameters for better localhost experience
          shareUrl = `https://www.linkedin.com/sharing/share-offsite/?` + 
            `url=${encodeURIComponent(storyUrl)}` + 
            `&title=${encodeURIComponent(story?.title || 'My TaleCraft Story')}` +
            `&summary=${encodeURIComponent(customCaption || story?.description || 'Check out this story I created with TaleCraft!')}` +
            `&source=${encodeURIComponent('TaleCraft')}`;
          break;
        default:
          throw new Error('Invalid platform selected');
      }

      // Check if a share record already exists for this story, user, and platform
      const { data: existingShare, error: checkError } = await supabase
        .from('story_shares')
        .select('id')
        .eq('story_id', storyId)
        .eq('shared_by_user_id', session.user.id)
        .eq('platform', selectedPlatform)
        .maybeSingle();

      if (checkError) {
        logger.error('Error checking for existing share:', checkError);
      }

      // Only insert a new record if no existing share was found
      if (!existingShare) {
        logger.log('No existing share found, creating new record');
        
        // Record the share
        const { error: shareError } = await supabase
          .from('story_shares')
          .insert({
            story_id: storyId,
            shared_by_user_id: session.user.id,
            platform: selectedPlatform,
            share_url: shareUrl,
            custom_caption: customCaption
          });

        if (shareError) {
          // Log but don't throw error - still allow sharing even if recording fails
          logger.error('Failed to record share:', shareError);
        }
      } else {
        logger.log('Existing share found, skipping DB insert');
      }

      // Open the platform's share dialog
      window.open(shareUrl, '_blank');
      
      toast({
        title: t('story.sharing.successTitle'),
        description: t('story.sharing.successMessage'),
      });
      
      setShareDialogOpen(false);
    } catch (error: any) {
      logger.error('Share process error:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to share the story. Please try again.",
      });
    } finally {
      setIsSharing(false);
    }
  };

  const handleContinueStory = async () => {
    if (!isFeatureEnabled('story_features', 'sequels')) {
      setIsContinueUpgradeDialogOpen(true);
      return;
    }
    if (!storyId) return;
    
    // Set loading state to true before starting the process
    setIsGeneratingContinuation(true);
    
    toast({
      title: t('story.generatingSummary'),
      description: t('story.pleaseWait'),
    });

    try {
      const { data, error } = await supabase.functions.invoke('generate-story-summary', {
        body: { storyId }
      });

      if (error) throw error;

      await supabase
        .from('stories')
        .update({ has_continuation: true })
        .eq('id', storyId);

      // Set loading state to false before navigation
      setIsGeneratingContinuation(false);
      
      navigate(`/story/new?parentId=${storyId}`, { 
        state: { 
          continuation: {
            parentStoryId: storyId,
            summary: data.summary,
            previousParts: data.previousParts,
            baseStory: data.story,
          }
        }
      });
    } catch (error) {
      logger.error('Error generating story summary:', error);
      // Set loading state to false on error
      setIsGeneratingContinuation(false);
      
      toast({
        variant: "destructive",
        title: t('errors.error'),
        description: t('errors.summaryError'),
      });
    }
  };

  const handlePrint = (viewMode: "paginated" | "entire") => {
    // Create a new window with properly formatted content for printing
    // Always print the entire content regardless of current view mode
    const printWindow = window.open("", "_blank");
    if (!printWindow || !storyContent) return;
    
    // Start building HTML document with styling
    let printHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${story?.title || 'Story'}</title>
        <style>
          body {
            font-family: 'Georgia', serif;
            line-height: 1.6;
            max-width: 800px;
            margin: 0 auto;
            padding: 20px;
            color: #333;
          }
          h1 {
            font-size: 24pt;
            text-align: center;
            margin-bottom: 30px;
            color: #444;
          }
          .story-part {
            margin-bottom: 30px;
          }
          .story-content {
            white-space: pre-wrap;
            text-align: justify;
            margin-bottom: 20px;
          }
          .user-choice {
            font-style: italic;
            color: #7E69AB;
            margin-top: 10px;
            margin-bottom: 20px;
            border-left: 3px solid #7E69AB;
            padding-left: 10px;
          }
          .story-image {
            max-width: 100%;
            max-height: 300px;
            display: block;
            margin: 20px auto;
          }
          .part-divider {
            border: none;
            border-top: 1px solid #ddd;
            margin: 30px 0;
          }
          @media print {
            body {
              padding: 0;
            }
            .story-image {
              max-height: 250px;
            }
          }
        </style>
      </head>
      <body>
        <h1>${story?.title || 'Story'}</h1>
    `;
  
    // Add each part of the story with proper formatting
    storyContent.forEach((part, index) => {
      printHtml += `<div class="story-part">`;
      
      // Add image if it exists
      if (part.image_url) {
        printHtml += `<img class="story-image" src="${part.image_url}" alt="Story illustration ${index + 1}">`;
      }
      
      // Format content with proper paragraph breaks
      const formattedContent = part.content
        .split('\n')
        .filter(paragraph => paragraph.trim() !== '')
        .map(paragraph => `<p>${paragraph}</p>`)
        .join('');
      
      printHtml += `<div class="story-content">${formattedContent}</div>`;
      
      // Add user choice if it exists
      if (part.user_input) {
        printHtml += `<div class="user-choice">Your choice: ${part.user_input}</div>`;
      }
      
      // Add a divider between parts, but not after the last one
      if (index < storyContent.length - 1) {
        printHtml += `<hr class="part-divider">`;
      }
      
      printHtml += `</div>`;
    });
    
    // Close HTML document
    printHtml += `
        </body>
      </html>
    `;
    
    // Write the HTML to the new window and print
    printWindow.document.write(printHtml);
    printWindow.document.close();
    
    // Wait for images to load before printing
    printWindow.onload = function() {
      printWindow.print();
    };
  };

  const isCreator = session?.user?.id === story?.user_id;
  const isLoading = isStoryLoading;

  // Add userProfile query for character extraction
  const { data: userProfile } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data: profile } = await supabase
        .from('profiles')
        .select('ai_provider')
        .eq('id', user.id)
        .single();

      return profile;
    }
  });

  // Function to generate audio for the current page
  const generateAudio = async () => {
    if (userCredits < requiredCredits) {
      toast({
        title: t('audioGeneration.insufficientCredits'),
        description: `${t('audioGeneration.needMoreCredits')} (${userCredits}/${requiredCredits})`,
        variant: "destructive"
      });
      return;
    }
    
    if (!storyContent || currentPage < 0 || !storyContent[currentPage]) {
      toast({
        title: t('audioGeneration.failed'),
        description: t('audioGeneration.contentNotFound'),
        variant: "destructive"
      });
      return;
    }
    
    const currentContentItem = storyContent[currentPage];
    
    // Debug log to help troubleshooting
    logger.log("Generating audio for:", {
      pageIndex: currentPage,
      contentId: currentContentItem.id,
      contentText: currentContentItem.content?.substring(0, 50) + "...",
      hasContent: !!currentContentItem.content
    });
    
    setIsGeneratingAudio(true);
    setAudioGenerationError(null);
    
    try {
      const response = await supabase.functions.invoke('generate-story-audio', {
        body: {
          storyId,
          pageId: currentContentItem.id,
          content: currentContentItem.content,
          language: story?.language || 'en'
        }
      });
      
      if (response.error) {
        throw new Error(response.error.message || 'Unknown error generating audio');
      }
      
      if (response.data?.audio?.audio_url) {
        setAudioUrl(response.data.audio.audio_url);
        setUserCredits(prev => prev - requiredCredits);
        
        // Update our local state to reflect the new audio URL
        const updatedContent = [...storyContent];
        updatedContent[currentPage] = {
          ...updatedContent[currentPage],
          audio_url: response.data.audio.audio_url
        };
        
        toast({
          title: t('audioGeneration.success'),
          description: t('audioGeneration.audioReady'),
        });
      } else {
        throw new Error('No audio URL received from the server');
      }
    } catch (error) {
      logger.error('Error generating audio:', error);
      setAudioGenerationError(error.message || 'Failed to generate audio');
      
      toast({
        title: t('audioGeneration.failed'),
        description: error.message || t('audioGeneration.unexpectedError'),
        variant: "destructive"
      });
    } finally {
      setIsGeneratingAudio(false);
    }
  };
  
  // Function to generate audio for a specific page
  const generateAudioForPage = async (pageIndex: number, pageContent: any) => {
    if (userCredits < requiredCredits) {
      toast({
        title: t('audioGeneration.insufficientCredits'),
        description: `${t('audioGeneration.needMoreCredits')} (${userCredits}/${requiredCredits})`,
        variant: "destructive"
      });
      return;
    }
    
    if (!pageContent || !pageContent.content) {
      toast({
        title: t('audioGeneration.failed'),
        description: t('audioGeneration.contentNotFound'),
        variant: "destructive"
      });
      return;
    }
    
    // Set current page for UI updates
    setCurrentPage(pageIndex);
    
    // Debug log to verify we're using the correct content
    logger.log("Generating audio for specific page:", {
      pageIndex,
      contentId: pageContent.id,
      contentText: pageContent.content.substring(0, 50) + "...",
      hasContent: !!pageContent.content
    });
    
    setIsGeneratingAudio(true);
    setAudioGenerationError(null);
    
    try {
      const response = await supabase.functions.invoke('generate-story-audio', {
        body: {
          storyId,
          pageId: pageContent.id,
          content: pageContent.content,
          language: story?.language || 'en'
        }
      });
      
      if (response.error) {
        throw new Error(response.error.message || 'Unknown error generating audio');
      }
      
      if (response.data?.audio?.audio_url) {
        setAudioUrl(response.data.audio.audio_url);
        setUserCredits(prev => prev - requiredCredits);
        
        // Update local cache of content
        if (storyContent) {
          const updatedContent = [...storyContent];
          updatedContent[pageIndex] = {
            ...updatedContent[pageIndex],
            audio_url: response.data.audio.audio_url
          };
        }
        
        toast({
          title: t('audioGeneration.success'),
          description: t('audioGeneration.audioReady'),
        });
      } else {
        throw new Error('No audio URL received from the server');
      }
    } catch (error) {
      logger.error('Error generating audio:', error);
      setAudioGenerationError(error.message || 'Failed to generate audio');
      
      toast({
        title: t('audioGeneration.failed'),
        description: error.message || t('audioGeneration.unexpectedError'),
        variant: "destructive"
      });
    } finally {
      setIsGeneratingAudio(false);
    }
  };

  // Function to generate multiple images
  const generateMultipleImages = async () => {
    if (userCredits < requiredCredits) {
      toast({
        title: t('imageGeneration.insufficientCredits'),
        description: `${t('imageGeneration.needMoreCredits')} (${userCredits}/${requiredCredits})`,
        variant: "destructive"
      });
      return;
    }
    
    if (!storyContent || currentPage < 0 || !storyContent[currentPage]) {
      toast({
        title: t('imageGeneration.failed'),
        description: t('imageGeneration.contentNotFound'),
        variant: "destructive"
      });
      return;
    }
    
    const currentContentItem = storyContent[currentPage];
    
    // Debug log to help troubleshooting
    logger.log("Generating image for:", {
      pageIndex: currentPage,
      contentId: currentContentItem.id,
      contentText: currentContentItem.content?.substring(0, 50) + "...",
      hasContent: !!currentContentItem.content
    });
    
    setIsGeneratingImage(true);
    setImageGenerationProgress(0);
    setGeneratedImages([]);
    setGenerationErrors([]);
    
    try {
      // Store the original image URL to restore it if the user cancels
      setOriginalImageUrl(currentContentItem.image_url || null);
      
      // Only hide the existing image in the UI during generation, but don't modify database
      setStoryImage(null);
      
      // Generate unique seeds for each image
      const seeds = Array.from({ length: NUM_IMAGES_TO_GENERATE }, () => Math.floor(Math.random() * 2147483647));
      
      // Create an array of promises for parallel execution
      const imagePromises = seeds.map((seed, i) => 
        supabase.functions.invoke('generate-story-image', {
          body: {
            storyId,
            pageId: currentContentItem.id,
            content: currentContentItem.content, // Ensure this is being passed correctly
            characters: story?.characters || [],
            storyData: story,
            isMultipleGeneration: true,
            imageIndex: i,
            totalImages: NUM_IMAGES_TO_GENERATE,
            seed: seed,
            skipDatabaseUpdate: true  // Add this flag to prevent automatic database update
          }
        })
      );
      
      // Set up a counter for completed requests to track progress
      let completedCount = 0;
      
      // Use Promise.allSettled to handle both successful and failed requests
      const results = await Promise.allSettled(
        imagePromises.map(async (promise, index) => {
          try {
            const result = await promise;
            // Update progress after each completion
            completedCount++;
            setImageGenerationProgress(completedCount);
            return { index, result };
          } catch (error) {
            // Also count errors as completed for progress
            completedCount++;
            setImageGenerationProgress(completedCount);
            throw { index, error };
          }
        })
      );
      
      // Process results
      const images: {id: string, url: string}[] = [];
      const errors: string[] = [];
      
      // Update credits after first successful generation
      let hasDeductedCredits = false;
      
      results.forEach((result, i) => {
        if (result.status === 'fulfilled') {
          const { index, result: apiResult } = result.value;
          
          if (apiResult.error) {
            errors.push(`Image ${index+1}: ${apiResult.error}`);
          } else if (apiResult.data?.error === 'Insufficient credits') {
            errors.push(`Image ${index+1}: ${t('imageGeneration.insufficientCredits')}`);
          } else if (apiResult.data?.image?.image_url) {
            images.push({
              id: `img-${index+1}`,
              url: apiResult.data.image.image_url
            });
            
            // Update credits display after first successful generation
            if (!hasDeductedCredits) {
              setUserCredits(prev => prev - requiredCredits);
              hasDeductedCredits = true;
            }
          } else {
            errors.push(`Image ${index+1}: ${t('imageGeneration.unexpectedError')}`);
          }
        } else if (result.status === 'rejected') {
          const { index, error } = result.reason;
          errors.push(`Image ${index+1}: ${error instanceof Error ? error.message : String(error)}`);
        }
      });
      
      setIsGeneratingImage(false);
      
      if (images.length === 0) {
        // All generations failed
        toast({
          title: t('imageGeneration.failed'),
          description: t('imageGeneration.allGenerationsFailed'),
          variant: "destructive"
        });
        return;
      }
      
      // Store the successfully generated images
      setGeneratedImages(images);
      setGenerationErrors(errors);
      
      // Open the selection modal
      setIsImageSelectionOpen(true);
      
      // If we got some images but had errors too
      if (errors.length > 0) {
        toast({
          title: t('imageGeneration.partialSuccess'),
          description: `${t('imageGeneration.someImagesFailed')} (${images.length}/${NUM_IMAGES_TO_GENERATE})`,
          variant: "default"
        });
      } else {
        toast({
          title: t('imageGeneration.ready'),
          description: t('imageGeneration.selectPreferred'),
        });
      }
    } catch (error) {
      logger.error('Error in parallel image generation:', error);
      toast({
        title: t('imageGeneration.failed'),
        description: t('imageGeneration.unexpectedError'),
        variant: "destructive"
      });
      setIsGeneratingImage(false);
    }
  };
  
  // Add a new function that takes the page index and content directly
const generateImageForPage = async (pageIndex: number, pageContent: any) => {
  if (userCredits < requiredCredits) {
    toast({
      title: t('imageGeneration.insufficientCredits'),
      description: `${t('imageGeneration.needMoreCredits')} (${userCredits}/${requiredCredits})`,
      variant: "destructive"
    });
    return;
  }
  
  if (!pageContent || !pageContent.content) {
    toast({
      title: t('imageGeneration.failed'),
      description: t('imageGeneration.contentNotFound'),
      variant: "destructive"
    });
    return;
  }
  
  // Set current page for UI updates
  setCurrentPage(pageIndex);
  
  // Debug log to verify we're using the correct content
  logger.log("Generating image for specific page:", {
    pageIndex,
    contentId: pageContent.id,
    contentText: pageContent.content.substring(0, 50) + "...",
    hasContent: !!pageContent.content,storyId,
    pageId: pageContent.id,
    content: pageContent.content, // Using the passed content directly
    characters: story?.characters || [],
    storyData: story,
    isMultipleGeneration: true,
    totalImages: NUM_IMAGES_TO_GENERATE,
    skipDatabaseUpdate: true
  });
  
  setIsGeneratingImage(true);
  setImageGenerationProgress(0);
  setGeneratedImages([]);
  setGenerationErrors([]);
  
  try {
    // Store the original image URL to restore it if user cancels
    setOriginalImageUrl(pageContent.image_url || null);
    
    // Hide existing image during generation
    setStoryImage(null);
    
    // Generate unique seeds for each image
    const seeds = Array.from({ length: NUM_IMAGES_TO_GENERATE }, () => Math.floor(Math.random() * 2147483647));
    
    // Create array of promises for parallel execution
    const imagePromises = seeds.map((seed, i) => 
      supabase.functions.invoke('generate-story-image', {
        body: {
          storyId,
          pageId: pageContent.id,
          content: pageContent.content, // Using the passed content directly
          characters: story?.characters || [],
          storyData: story,
          isMultipleGeneration: true,
          imageIndex: i,
          totalImages: NUM_IMAGES_TO_GENERATE,
          seed: seed,
          skipDatabaseUpdate: true
        }
      })
    );
    
    // Rest of the image generation process is the same
    // ...existing code for handling promises and results...
    let completedCount = 0;
    
    // Use Promise.allSettled to handle both successful and failed requests
    const results = await Promise.allSettled(
      imagePromises.map(async (promise, index) => {
        try {
          const result = await promise;
          // Update progress after each completion
          completedCount++;
          setImageGenerationProgress(completedCount);
          return { index, result };
        } catch (error) {
          // Also count errors as completed for progress
          completedCount++;
          setImageGenerationProgress(completedCount);
          throw { index, error };
        }
      })
    );
    
    // Process results
    const images: {id: string, url: string}[] = [];
    const errors: string[] = [];
    
    // Update credits after first successful generation
    let hasDeductedCredits = false;
    
    results.forEach((result, i) => {
      if (result.status === 'fulfilled') {
        const { index, result: apiResult } = result.value;
        
        if (apiResult.error) {
          errors.push(`Image ${index+1}: ${apiResult.error}`);
        } else if (apiResult.data?.error === 'Insufficient credits') {
          errors.push(`Image ${index+1}: ${t('imageGeneration.insufficientCredits')}`);
        } else if (apiResult.data?.image?.image_url) {
          images.push({
            id: `img-${index+1}`,
            url: apiResult.data.image.image_url
          });
          
          // Update credits display after first successful generation
          if (!hasDeductedCredits) {
            setUserCredits(prev => prev - requiredCredits);
            hasDeductedCredits = true;
          }
        } else {
          errors.push(`Image ${index+1}: ${t('imageGeneration.unexpectedError')}`);
        }
      } else if (result.status === 'rejected') {
        const { index, error } = result.reason;
        errors.push(`Image ${index+1}: ${error instanceof Error ? error.message : String(error)}`);
      }
    });
    
    setIsGeneratingImage(false);
    
    if (images.length === 0) {
      toast({
        title: t('imageGeneration.failed'),
        description: t('imageGeneration.allGenerationsFailed'),
        variant: "destructive"
      });
      return;
    }
    
    setGeneratedImages(images);
    setGenerationErrors(errors);
    
    setIsImageSelectionOpen(true);
    
    if (errors.length > 0) {
      toast({
        title: t('imageGeneration.partialSuccess'),
        description: `${t('imageGeneration.someImagesFailed')} (${images.length}/${NUM_IMAGES_TO_GENERATE})`,
        variant: "default"
      });
    } else {
      toast({
        title: t('imageGeneration.ready'),
        description: t('imageGeneration.selectPreferred'),
      });
    }
  } catch (error) {
    logger.error('Error in parallel image generation:', error);
    toast({
      title: t('imageGeneration.failed'),
      description: t('imageGeneration.unexpectedError'),
      variant: "destructive"
    });
    setIsGeneratingImage(false);
  }
};

  // Function to handle image selection and save
  const handleImageSelect = async (imageId: string) => {
    setSelectedImageId(imageId);
  };
  
  // Function to confirm selection and save to database
  const handleConfirmSelection = async () => {
    if (!selectedImageId || !storyContent || currentPage < 0 || !storyContent[currentPage]) return;
    
    const selectedImage = generatedImages.find(img => img.id === selectedImageId);
    if (!selectedImage) return;
    
    try {
      // Update the story content with the selected image URL
      const { error } = await supabase
        .from('story_content')
        .update({ image_url: selectedImage.url })
        .eq('id', storyContent[currentPage].id);
        
      if (error) {
        toast({
          title: t('imageGeneration.saveFailed'),
          description: error.message,
          variant: "destructive"
        });
        // Restore original image if save fails
        setStoryImage(originalImageUrl);
        return;
      }
      
      // Set the selected image as the story image for paginated view
      setStoryImage(selectedImage.url);
      
      // Also update the local storyContent state to update the UI in entire view mode
      if (storyContent) {
        const updatedContent = [...storyContent];
        updatedContent[currentPage] = {
          ...updatedContent[currentPage],
          image_url: selectedImage.url
        };
        
        // Update local state directly
        const storyContentKey = ['story-content', storyId];
        const cachedData = storyContent;
        
        if (cachedData) {
          // Manually updating the content array to reflect changes immediately in UI
          cachedData[currentPage].image_url = selectedImage.url;
        }
      }
      
      // Close the selection modal
      setIsImageSelectionOpen(false);
      setGeneratedImages([]);
      setSelectedImageId(null);
      
      toast({
        title: t('imageGeneration.saved'),
        description: t('imageGeneration.imageSaved'),
      });
      
      // Refresh the story content from the server
      const freshData = await onContentUpdate();
      if (freshData) {
        // Manually refresh storyContent with the fresh data
        const storyContentKey = ['story-content', storyId];
      }
    } catch (error) {
      logger.error('Error saving selected image:', error);
      toast({
        title: t('imageGeneration.saveFailed'),
        description: t('imageGeneration.unexpectedError'),
        variant: "destructive"
      });
      
      // Restore original image if save fails
      setStoryImage(originalImageUrl);
    }
  };

  // Function to handle cancellation of image selection
  const handleCancelSelection = async () => {
    // Restore the original image in the UI
    setStoryImage(originalImageUrl);
    
    // Ensure the database still has the original image
    if (originalImageUrl !== storyContent?.[currentPage]?.image_url && storyContent?.[currentPage]?.id) {
      try {
        await supabase
          .from('story_content')
          .update({ image_url: originalImageUrl })
          .eq('id', storyContent[currentPage].id);
      } catch (error) {
        logger.error('Error restoring original image in database:', error);
      }
    }
    
    setIsImageSelectionOpen(false);
    setSelectedImageId(null);
    setGeneratedImages([]);
  };

  // Function to refresh content data
  const onContentUpdate = async () => {
    try {
      const { data, error } = await supabase
        .from('story_content')
        .select('*')
        .eq('story_id', storyId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      // No need to set state directly as react-query will handle refresh
      return data;
    } catch (error) {
      logger.error("Error refreshing content:", error);
      return null;
    }
  };

  // Initialize currentPage based on URL parameter if present
  useEffect(() => {
    // Check if there's a page parameter in the URL
    const pageParam = searchParams.get('page');
    if (pageParam && storyContent) {
      // Convert to number
      let pageNumber = parseInt(pageParam, 10);
      
      // If we have a page=10 in URL for a 10-page story, we need to show the last page (index 9)
      // Handle both cases: 1-based page numbers (page=10) or 0-based indices (page=9)
      if (pageNumber === storyContent.length) {
        // This is likely a 1-based page number, so convert to 0-based index
        pageNumber = pageNumber - 1;
      }
      
      // Make sure it's within valid range
      if (!isNaN(pageNumber) && pageNumber >= 0 && pageNumber < storyContent.length) {
        // Set the current page to the one specified in URL
        setCurrentPage(pageNumber);
        logger.log(`Setting current page to ${pageNumber} from URL parameter (original: ${pageParam})`);
      } else {
        logger.log(`Invalid page number in URL: ${pageParam}, defaulting to page 0`);
      }
    }
  }, [searchParams, storyContent]);

  if (isLoading) {
    return (
      <div className="min-h-screen relative pb-16">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1A1F2C] to-[#7E69AB]" />
        <div className="absolute inset-0 bg-[url('/background.webp')] bg-cover bg-center opacity-10" />
        <div className="relative max-w-4xl mx-auto p-4 sm:p-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-white/10 rounded w-1/4"></div>
            <div className="h-4 bg-white/10 rounded w-3/4"></div>
            <div className="h-4 bg-white/10 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  // Add view mode rendering logic to the return statement
  if (isGeneratingContinuation) {
    return (
      <div className="min-h-screen relative pb-16">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1A1F2C] to-[#7E69AB]" />
        <div className="absolute inset-0 bg-[url('/background.webp')] bg-cover bg-center opacity-10" />
        
        <div className="relative">
          <NavigationBar session={session} />
          
          <div className="max-w-4xl mx-auto p-4 sm:p-8">
            <h2 className="text-3xl font-bold text-white mb-8">{t('story.generatingContinuation')}</h2>
            <SmartLoadingSection />
          </div>
          
          <BottomNavigation session={session} />
        </div>
      </div>
    );
  }

  // Rest of the component remains the same
  return (
    <div className="min-h-screen relative pb-16">
      <div className="absolute inset-0 bg-gradient-to-br from-[#1A1F2C] to-[#7E69AB]" />
      <div className="absolute inset-0 bg-[url('/background.webp')] bg-cover bg-center opacity-10" />
      
      <div className="relative">
        <NavigationBar session={session} />
        
        <div className="max-w-4xl mx-auto p-2 md:p-4">
          <div className="flex justify-between items-center w-full">
            <div className="flex gap-2 w-full">
              <StoryHeader
                onPrint={handlePrint}
                onContinueStory={handleContinueStory}
                isFinished={!!story?.finished_at}
                hasNoContinuation={!story?.has_continuation}
                isCreator={isCreator}
                finished_at={story?.finished_at}
                canContinue={isFeatureEnabled('story_features', 'sequels')}
                canShare={isFeatureEnabled('story_features', 'share')}
                onShareStory={handleShareClick}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
              />
            </div>
          </div>

          {/* Image Selection Modal */}
          <Dialog 
            open={isImageSelectionOpen} 
            onOpenChange={(open) => {
              if (!open) {
                // If the dialog is closing (Cancel clicked or clicked outside), restore the original
                handleCancelSelection();
              }
              setIsImageSelectionOpen(open);
            }}
          >
            <DialogContent className="sm:max-w-4xl">
              <DialogHeader>
                <DialogTitle>{t('imageGeneration.selectImage')}</DialogTitle>
                <DialogDescription>
                  {t('imageGeneration.selectImageDescription')}
                </DialogDescription>
              </DialogHeader>
              
              <div className="grid grid-cols-2 gap-4 mt-4">
                {generatedImages.map((image) => (
                  <div 
                    key={image.id}
                    className={`relative border-2 rounded-md overflow-hidden cursor-pointer transition-all
                      ${selectedImageId === image.id ? 'border-primary ring-2 ring-primary' : 'border-gray-200 hover:border-gray-300'}`}
                    onClick={() => handleImageSelect(image.id)}
                  >
                    <img 
                      src={image.url} 
                      alt="Generated illustration option" 
                      className="w-full h-64 object-cover"
                    />
                    {selectedImageId === image.id && (
                      <div className="absolute top-2 right-2 bg-primary text-primary-foreground rounded-full p-1">
                        <Check className="h-5 w-5" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              {generationErrors.length > 0 && (
                <div className="mt-4 text-sm text-amber-600">
                  <p>{t('imageGeneration.someImagesFailed')}</p>
                </div>
              )}
              
              <div className="flex justify-end gap-2 mt-4">
                <Button 
                  variant="outline" 
                  onClick={handleCancelSelection}
                >
                  {t('cancel')}
                </Button>
                <Button 
                  onClick={handleConfirmSelection}
                  disabled={!selectedImageId}
                >
                  {t('confirm')}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {story?.finished_at && (
            <div className="mb-6 flex items-center justify-center gap-2 bg-white/5 backdrop-blur-sm p-4 rounded-lg">
              {[1, 2, 3, 4, 5].map((star) => (
                <Button
                  key={star}
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRate(star)}
                  disabled={isRating || !session}
                  className="p-1 hover:scale-110 transition-transform w-full"
                >
                  {star <= (userRating || 0) ? (
                    <StarIcon className="h-6 w-6 text-yellow-400 fill-yellow-400" />
                  ) : (
                    <Star className="h-6 w-6 text-white/40" />
                  )}
                </Button>
              ))}
              {averageRating !== null && (
                <span className="text-sm text-white/60 ml-2">
                  ({averageRating.toFixed(1)})
                </span>
              )}
            </div>
          )}

          <StoryNavigation
            storyParts={storyParts}
            currentStoryId={storyId || ''}
            onPartClick={(partId) => navigate(`/read/${partId}`)}
          />

          {/* Conditional rendering based on viewMode */}
          {viewMode === "entire" ? (
            // Entire story view mode with same styling as paginated view
            <div className="bg-white rounded-lg shadow-xl p-4 md:p-8 print:shadow-none print:border-none">
              <h1 className="text-muted-foreground text-3xl font-bold mb-12 text-center">{story?.title || ''}</h1>
              <div className="prose prose-lg max-w-none mb-8">
                {storyContent?.map((content, index) => (
                  <div key={content.id} className="mb-8 relative pt-6"> 
                    {/* Replace with toolbar component for each page in entire mode */}
                    {isCreator && (
                      <div className="absolute top-0 right-0 z-10 mt-2">
                        <MediaGenerationToolbar 
                          onGenerateImage={() => generateImageForPage(index, content)}
                          onGenerateAudio={() => generateAudioForPage(index, content)}
                          isGeneratingImage={isGeneratingImage && currentPage === index}
                          isGeneratingAudio={isGeneratingAudio && currentPage === index}
                          userCredits={userCredits}
                          requiredCredits={requiredCredits}
                          hasImage={!!content.image_url}
                          hasAudio={!!content.audio_url}
                          disableAudio={story?.language === "sl"}
                          t={t}
                          isLastPage={false}
                        />
                      </div>
                    )}
                    
                    <div className="mt-6"> {/* Added mt-6 for additional spacing */}
                      {content.image_url && (
                        <div className="mb-6">
                          <img 
                            src={content.image_url} 
                            alt={`Illustration for part ${index + 1}`} 
                            className="rounded-md mx-auto max-h-100 md:max-h-80 shadow-md"
                          />
                        </div>
                      )}
                      
                      {/* Audio player for this content if available */}
                      {content.audio_url && (
                        <div className="mb-6 mt-2">
                          <audio 
                            controls 
                            src={content.audio_url} 
                            className="w-full rounded-md shadow-sm"
                          >
                            Your browser does not support the audio element.
                          </audio>
                        </div>
                      )}
                      
                      <div className="text-gray-800 leading-relaxed text-justify">
                        {content.content}
                      </div>
                      {/* Add user choice display */}
                      {content.user_input && (
                        <p className="text-primary font-medium mt-2 italic text-justify">
                          {t('storyEditor.choices.yourChoice')}: {content.user_input}
                        </p>
                      )}
                      
                      <div className="text-sm text-gray-500 text-center mt-4">
                        {t('storyEditor.navigation.page')} {index + 1} {t('storyEditor.navigation.of')} {storyContent.length}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Add character extraction component */}
              {isCreator && userProfile?.ai_provider && (
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <ExtractedCharacters 
                    storyId={storyId || ''} 
                    aiProvider={userProfile.ai_provider}
                    canExtractCharacter={isFeatureEnabled('story_features', 'extract_characters')}
                  />
                </div>
              )}
            </div>
          ) : (
            <StoryContent
              title={story?.title || ''}
              content={storyContent || []}
              storyId={storyId || ''}
              showCharacterExtraction={isCreator}
              canExtractCharacter={isFeatureEnabled('story_features', 'extract_characters')}
              onPageChange={setCurrentPage}
              viewMode={viewMode}
              // Pass the necessary props for media toolbar
              isCreator={isCreator}
              onGenerateImage={generateMultipleImages}
              onGenerateAudio={generateAudio}
              isGeneratingImage={isGeneratingImage}
              isGeneratingAudio={isGeneratingAudio}
              userCredits={userCredits}
              requiredCredits={requiredCredits}
              storyImage={storyImage}
              audioUrl={audioUrl}
              imageGenerationProgress={imageGenerationProgress}
              numImagesToGenerate={NUM_IMAGES_TO_GENERATE}
              disableAudio={story?.language === "sl"}
              t={t}
              // Pass rewrite functionality props
              rewritesAvailable={storyRewritesAvailable}
              isRewriting={isRewriting}
              onRewritten={handleRewritten}
              story={story} // Pass full story object
            />
          )}
        </div>

        <BottomNavigation session={session} />
      </div>

      <Dialog open={isContinueUpgradeDialogOpen} onOpenChange={setIsContinueUpgradeDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('story.upgrade.title')}</DialogTitle>
            <DialogDescription>
              {t('story.upgrade.continueDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-primary" />
            <span>{t('story.upgrade.continueFeature')}</span>
          </div>
          <DialogFooter className="sm:justify-start">
            <Button
              type="button"
              onClick={() => {
                setIsContinueUpgradeDialogOpen(false);
                navigate("/dashboard/more");
              }}
              className="w-full sm:w-auto"
            >
              {t('story.upgrade.upgradeButton')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isShareUpgradeDialogOpen} onOpenChange={setIsShareUpgradeDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('story.upgrade.title')}</DialogTitle>
            <DialogDescription>
              {t('story.upgrade.shareDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-primary" />
            <span>{t('story.upgrade.shareFeature')}</span>
          </div>
          <DialogFooter className="sm:justify-start">
            <Button
              type="button"
              onClick={() => {
                setIsShareUpgradeDialogOpen(false);
                navigate("/dashboard/more");
              }}
              className="w-full sm:w-auto"
            >
              {t('story.upgrade.upgradeButton')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('story.sharing.dialogTitle')}</DialogTitle>
            <DialogDescription>
              {t('story.sharing.dialogDescription')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('story.sharing.privacy')}</label>
              <Select value={privacyLevel} onValueChange={setPrivacyLevel}>
                <SelectTrigger>
                  <SelectValue placeholder={t('story.sharing.selectPlatform')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="private">{t('story.sharing.privacyLevels.private')}</SelectItem>
                  <SelectItem value="public">{t('story.sharing.privacyLevels.public')}</SelectItem>
                  <SelectItem value="friends">{t('story.sharing.privacyLevels.friends')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">{t('story.sharing.platform')}</label>
              <Select value={selectedPlatform} onValueChange={setSelectedPlatform}>
                <SelectTrigger>
                  <SelectValue placeholder={t('story.sharing.selectPlatform')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="facebook">
                    <div className="flex items-center gap-2">
                      <SiFacebook className="h-4 w-4" />
                      {t('settings.profile.inviteFriends.socialPlatforms.facebook')}
                    </div>
                  </SelectItem>
                  <SelectItem value="twitter">
                    <div className="flex items-center gap-2">
                      <SiX className="h-4 w-4" />
                      {t('settings.profile.inviteFriends.socialPlatforms.X')}
                    </div>
                  </SelectItem>
                  <SelectItem value="linkedin">
                    <div className="flex items-center gap-2">
                      <SiLinkedin className="h-4 w-4" />
                      {t('settings.profile.inviteFriends.socialPlatforms.linkedin')}
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">{t('story.sharing.customMessage')}</label>
              <Input
                placeholder={t('story.sharing.customMessagePlaceholder')}
                value={customCaption}
                onChange={(e) => setCustomCaption(e.target.value)}
                maxLength={2000}
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button 
              onClick={handleShare} 
              disabled={!selectedPlatform || isSharing}
              className="gap-2 w-full"
            >
              {isSharing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Share2 className="h-4 w-4" />
              )}
              {t('story.sharing.shareButton')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <style>{`
        @media print {
          body {
            background: white;
          }
          .print\\:hidden {
            display: none;
          }
          .prose {
            max-width: none;
          }
        }
      `}</style>
    </div>
  );
};

export default ReadStory;
